import type { ListingCardListing } from '@/src/components/ListingCard/types';
import type { SimilarListing } from '@/src/components/features/property/hooks/useSimilarListings';
import type { ListingMinMaxPriceBeds } from '@/src/types/commonTypes.type';

export function getPostContactListingId(listing: SimilarListing) {
  return listing.aliasEncoded ?? listing.maloneLotIdEncoded ?? listing.lotIdEncoded ?? '';
}

export function getPostContactListingTitle(listing: SimilarListing) {
  return listing.displayName ?? listing.title ?? listing.name ?? listing.address?.street ?? 'Similar property';
}

function toNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
}

function compactUnique(values: Array<string | undefined>) {
  return Array.from(new Set(values.filter((value): value is string => Boolean(value))));
}

function getPhotoUrls(listing: SimilarListing) {
  return compactUnique([
    ...(listing.medPhotoUrls ?? []),
    listing.medPhotoUrl,
    ...(listing.photos ?? []).map((photo) => photo.url),
    listing.previewPhotoMed?.url,
    listing.previewPhoto?.url,
    listing.photoUrl,
    listing.thumbPhoto?.url,
    listing.thumbPhotoUrl,
  ]);
}

function getListingMinMaxPriceBeds(listing: SimilarListing): ListingMinMaxPriceBeds {
  const source = listing.listingMinMaxPriceBeds ?? {};
  const minPrice = toNumber(source.minPrice) ?? toNumber(listing.pricing?.summary?.priceLow) ?? 0;
  const maxPrice = toNumber(source.maxPrice) ?? toNumber(listing.pricing?.summary?.priceHigh) ?? minPrice;
  const minBeds = toNumber(source.minBeds) ?? 0;
  const maxBeds = toNumber(source.maxBeds) ?? minBeds;
  const minBaths = toNumber(source.minBaths) ?? 0;
  const maxBaths = toNumber(source.maxBaths) ?? minBaths;
  const minSqft = toNumber(source.minSqft) ?? 0;
  const maxSqft = toNumber(source.maxSqft) ?? minSqft;

  return {
    DATA_MODEL: source.DATA_MODEL ?? 'ListingMinMaxPriceBeds',
    minPrice,
    maxPrice,
    minBeds,
    maxBeds,
    minBaths,
    maxBaths,
    minSqft,
    maxSqft,
    bedDisplay: source.bedDisplay ?? '',
    bedDisplayAllAvailable: source.bedDisplayAllAvailable ?? '',
    bathDisplay: source.bathDisplay ?? '',
    priceDisplay: source.priceDisplay ?? '',
    priceDisplayRange: source.priceDisplayRange ?? '',
    sqftDisplay: source.sqftDisplay ?? '',
    bedDisplayCompact: source.bedDisplayCompact ?? '',
    bedDisplaySemiCompact: source.bedDisplaySemiCompact ?? '',
    bathDisplayCompact: source.bathDisplayCompact ?? '',
    bathDisplaySemiCompact: source.bathDisplaySemiCompact ?? '',
  };
}

export function toPostContactListingCardListing(listing: SimilarListing): ListingCardListing {
  const photoUrls = getPhotoUrls(listing);
  const aliasEncoded = listing.aliasEncoded ?? getPostContactListingId(listing);

  return {
    ...listing,
    active: listing.active ?? true,
    address: {
      street: listing.address?.street ?? '',
      city: listing.address?.city ?? '',
      state: listing.address?.state ?? '',
      zip: '',
      neighborhood: '',
      hideStreet: listing.address?.hideStreet ?? false,
    },
    aliasEncoded,
    amenities: listing.amenities ?? {},
    contact: {},
    geo: listing.geo ?? { lat: 0, lon: 0 },
    listedBy: {},
    listingMinMaxPriceBeds: getListingMinMaxPriceBeds(listing),
    listingType: listing.listingType ?? 'rental',
    lotIdEncoded: listing.lotIdEncoded ?? null,
    maloneLotIdEncoded: listing.maloneLotIdEncoded ?? listing.lotIdEncoded ?? aliasEncoded,
    medPhotoUrl: photoUrls[0],
    medPhotoUrls: photoUrls,
    photoCount: listing.photoCount ?? photoUrls.length,
    priority: listing.priority ?? 0,
    propertyType: listing.propertyType ?? 'apartment',
    rentalApplicationStatus: listing.rentalApplicationStatus ?? '',
    score: listing.score ?? 0,
    searchKeyword: listing.searchKeyword ?? '',
    title: getPostContactListingTitle(listing),
    trusted: listing.trusted ?? false,
    unit: listing.unit ?? '',
    uriMalone: listing.uriMalone ?? listing.uriV2 ?? listing.uriBuilding ?? '',
    userItemTypes: listing.userItemTypes ?? [],
    userItemDates: listing.userItemDates,
  } as ListingCardListing;
}
